import { useRef } from "react";

/**
 * Accessible tab strip (WAI-ARIA tabs pattern): roving tabindex, ←/→/Home/End move and activate
 * (↑/↓ for a vertical list, e.g. the Settings sections). Pair each panel with
 * `tabPanelProps(idBase, value)`.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idBase,
  variant = "underline",
  orientation = "horizontal",
  className,
}: {
  tabs: ReadonlyArray<readonly [T, React.ReactNode]>;
  value: T;
  onChange: (v: T) => void;
  label: string;
  idBase: string;
  variant?: "underline" | "segmented" | "pill" | "list";
  orientation?: "horizontal" | "vertical";
  className?: string;
}): React.JSX.Element {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (from: number, delta: number | "first" | "last"): void => {
    const n = tabs.length;
    const to = delta === "first" ? 0 : delta === "last" ? n - 1 : (from + delta + n) % n;
    const t = tabs[to];
    if (!t) return;
    onChange(t[0]);
    refs.current[to]?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      className={`tabs tabs-${variant}${className ? ` ${className}` : ""}`}
    >
      {tabs.map(([v, text], i) => (
        <button
          key={v}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={`${idBase}-tab-${v}`}
          aria-selected={v === value}
          aria-controls={`${idBase}-panel-${v}`}
          tabIndex={v === value ? 0 : -1}
          className={v === value ? "tab active" : "tab"}
          onClick={() => {
            onChange(v);
          }}
          onKeyDown={(e) => {
            const nextKey = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
            const prevKey = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
            if (e.key === nextKey) {
              e.preventDefault();
              move(i, 1);
            } else if (e.key === prevKey) {
              e.preventDefault();
              move(i, -1);
            } else if (e.key === "Home") {
              e.preventDefault();
              move(i, "first");
            } else if (e.key === "End") {
              e.preventDefault();
              move(i, "last");
            }
          }}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function tabPanelProps(
  idBase: string,
  value: string,
): { role: "tabpanel"; id: string; "aria-labelledby": string; tabIndex: 0 } {
  return {
    role: "tabpanel",
    id: `${idBase}-panel-${value}`,
    "aria-labelledby": `${idBase}-tab-${value}`,
    tabIndex: 0,
  };
}
